import { css } from "@emotion/react";
import { useState } from "react";
import { Link } from "react-router";
import { LoadingSpinner } from "../bits/loading";
import { useTitle } from "../hooks/useTitle";
import { trpc } from "../../utils/trpc";
import { breadcrumbItem, breadcrumbLink, frjBanner } from "../bits/frjUtils";
import { Checkbox } from "@mui/material";

export const FrjTagsListPage = () => {
  useTitle("FRJ Tags | Compass");
  const { data: tags, isLoading } = trpc.getFrjTags.useQuery();
  const { data: tagFilter, isLoading: isTagFilterLoading } =
    trpc.getFrjTagFilter.useQuery();
  const [popularOnly, setPopularOnly] = useState(true);
  const [search, setSearch] = useState("");

  const allowedNames = isTagFilterLoading
    ? new Set<string>()
    : tagFilter?.length
      ? new Set(tagFilter.map((n) => n.toLowerCase()))
      : null;

  const visibleTags = tags
    ?.filter(
      (tag) =>
        tag.slug &&
        !tag.slug.startsWith("hash-") &&
        (tag.count?.posts ?? 0) > 0 &&
        (allowedNames === null ||
          !popularOnly ||
          allowedNames.has(tag.name?.toLowerCase() ?? "")),
    )
    .filter((tag) => tag.name?.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <div css={frjBanner}>Browse articles by tag</div>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          padding: 24px;
          gap: 24px;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 12px;
          `}
        >
          <Link to="/frj" css={breadcrumbLink}>
            FRJ Articles
          </Link>
          <span
            css={css`
              color: rgba(18, 23, 42, 0.3);
              font-size: 13px;
            `}
          >
            /
          </span>
          <span css={breadcrumbItem}>Tags</span>
        </div>

        <h1
          css={css`
            font-family: Spectral;
            font-size: 28px;
            font-weight: bold;
            color: #12172a;
            margin: 0;
          `}
        >
          Browse by Tag
        </h1>
        <div
          css={css`
            display: flex;
            gap: 16px;
          `}
        >
          <input
            type="search"
            placeholder="Search tags…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            css={css`
              font-family: Urbanist;
              font-size: 14px;
              padding: 8px 12px;
              border: 1px solid rgba(0, 31, 43, 0.2);
              border-radius: 6px;
              outline: none;
              width: 280px;
              color: #12172a;
              &:focus {
                border-color: rgba(0, 31, 43, 0.5);
              }
              &::placeholder {
                color: rgba(18, 23, 42, 0.4);
              }
            `}
          />
          <div>
            <Checkbox
              checked={popularOnly}
              aria-label="Popular Tags"
              onChange={(e) => setPopularOnly(e.target.checked)}
            />
            Popular Tags
          </div>
        </div>

        {(isLoading || isTagFilterLoading) && <LoadingSpinner />}

        {visibleTags && (
          <div
            css={css`
              display: flex;
              flex-wrap: wrap;
              gap: 12px;
            `}
          >
            {visibleTags.map((tag) => (
              <Link
                key={tag.id}
                to={`/frj/tag/${tag.slug}`}
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  padding: 10px 16px;
                  border: 1px solid rgba(0, 31, 43, 0.15);
                  border-radius: 6px;
                  text-decoration: none;
                  background: white;
                  transition:
                    border-color 0.15s ease,
                    background 0.15s ease;
                  &:hover {
                    border-color: rgba(0, 31, 43, 0.4);
                    background: rgba(0, 31, 43, 0.03);
                  }
                `}
              >
                <span
                  css={css`
                    font-family: Urbanist;
                    font-size: 14px;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                    color: #353d66;
                  `}
                >
                  {tag.name}
                </span>
                {tag.count?.posts != null && (
                  <span
                    css={css`
                      font-family: Urbanist;
                      font-size: 12px;
                      font-weight: 500;
                      color: rgba(18, 23, 42, 0.45);
                    `}
                  >
                    {tag.count.posts}
                  </span>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
};
